import "./ExamPage.css";
import Svgify from "@sumcode/svgify";
import useExam from "../../helpers/useExam";
import { useState, useRef, useEffect } from "react";
import useExamControls from "./Controls/userExamControls";
import Modal from "../../../../components/Modal/Modal";
import ScratchPadModal from "../../../../components/IFRS/ScratchPadModal";
import PDFViewerModal from "../../../../components/IFRS/PDFViewerModal";
import WordProcessorModal from "../../../../components/IFRS/WordProcessorModal";
import "jsuites/dist/jsuites.css";
import "jspreadsheet-ce/dist/jspreadsheet.css";
import { Spreadsheet, Worksheet } from "@jspreadsheet-ce/react";
import { IFRSQuestionExhibitDTO } from "../../../../api/Quiz/GetIFRSQuestionExhibits";
import { IFRSQuestionResponseOptionDTO } from "../../../../api/Quiz/GetIFRSQuestionResponseOptions";
import { SubmitIFRSQuestionResponse } from "../../../../api/Quiz/SubmitIFRSQuestionResponse";
import { EndIFRSExam } from "../../../../api/Quiz/EndIFRSExam";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import Spinner from "../../../../components/Shared/Spinner";
import SpreadsheetFileUpload from "../../../../components/Shared/SpreadsheetFileUpload";
import { axios } from "../../../../lib/axios";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

const ExamPage = () => {
  const navigate = useNavigate();
  const {
    hightlight,
    calculator,
    spreadsheet,
    scratchPad,
    wordProcessor,
    strikethrough,
    symbol,
  } = useExamControls();
  const [openExhibits, setOpenExhibits] = useState<Array<any>>([]);
  const spreadsheetRefs = useRef<Record<string, any>>({});

  const {
  examId: exam_id,
  currentQuestionId,
  studentExamId,
  isSeen,
  questions,
  exhibitsData,
  responseOptionsData,
  questionData,
  toggleFlag,
  isFlagged,
  isLastQuestion,
  isFirstQuestion,   // add this
  nextQuestion,
  prevQuestion,      // add this
  goToQuestion,
} = useExam();
const handlePrevQuestion = () => {
  closeAllWindows();
  prevQuestion();
};
  const [requirementsModalOpen, setRequirementsModalOpen] = useState(false);
  const [navigatorModalOpen, setNavigatorModalOpen] = useState(false);
  const [isSubmittingExam, setIsSubmittingExam] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentUploadOption, setCurrentUploadOption] =
    useState<IFRSQuestionResponseOptionDTO | null>(null);
      const exhibitFocusRef = useRef<Record<string, () => void>>({});

  const isQuestionFlagged = currentQuestionId
    ? isFlagged(currentQuestionId)
    : false;
  const checkQuestionFlagged = isFlagged;

  const sidebarRef = useRef<HTMLElement>(null);
const isResizingSidebar = useRef(false);

useEffect(() => {
  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizingSidebar.current || !sidebarRef.current) return;
    const newWidth = e.clientX - sidebarRef.current.getBoundingClientRect().left;
    if (newWidth > 200 && newWidth < 600) {
      sidebarRef.current.style.width = `${newWidth}px`;
    }
  };

  const handleMouseUp = () => {
    isResizingSidebar.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
  return () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };
}, []);

  const handleViewExhibit = (exhibit: IFRSQuestionExhibitDTO) => {
    if (exhibit.type === "spreadsheet") {
      if (exhibit.filePath) {
        const a = document.createElement("a");
        a.href = exhibit.filePath;
        a.download = exhibit.title || "exhibit.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        toast.error("No file available to download");
      }
      return;
    }

    // Check if exhibit is already open
    const alreadyOpen = openExhibits.find((e) => e.id === exhibit.id);
    if (alreadyOpen) {
      exhibitFocusRef.current[exhibit.id]?.();
      return;
    }

    setOpenExhibits((prev) => [
      ...prev,
      {
        id: exhibit.id,
        type: "pdf",
        pdfUrl: exhibit.filePath,
        title: exhibit.title,
      },
    ]);
  };

  const closeExhibit = (id: string) => {
    setOpenExhibits((prev) => prev.filter((e) => e.id !== id));
  };

  const handleOpenWordProcessor = async (
    option: IFRSQuestionResponseOptionDTO
  ) => {
    wordProcessor.setIsOpen(true);
    wordProcessor.setCurrentResponseOption(option);

    if (option.answerFileUrl) {
      wordProcessor.setIsLoading(true);
      try {
        const fileResponse = await axios.get(
          `word/parse-from-url?url=${encodeURIComponent(option.answerFileUrl)}`
        );
        const htmlContent = fileResponse.data?.data?.html;
        wordProcessor.setContent(htmlContent || "");
        if (htmlContent) toast.success("Document loaded successfully");
      } catch (error) {
        console.error("Error loading word processor file:", error);
        toast.error("Failed to load document");
        wordProcessor.setContent("");
      } finally {
        wordProcessor.setIsLoading(false);
      }
    } else {
      wordProcessor.setContent("");
    }
  };

  const closeAllWindows = () => {
    setOpenExhibits([]);
    spreadsheet.setIsOpen(false);
    scratchPad.setIsOpen(false);
    calculator.setIsOpen(false);
    hightlight.setIsOpen(false);
    wordProcessor.setIsOpen(false);
    symbol.setIsOpen(false);
    setRequirementsModalOpen(false);
    setNavigatorModalOpen(false);
  };

  // Navigation wrappers that close all windows before navigating
  const handleNextQuestion = () => {
    closeAllWindows();
    nextQuestion();
  };


  const handleGoToQuestion = (index: number) => {
    closeAllWindows();
    goToQuestion(index);
  };

  // Handle end exam (called from footer button on last question)
  const handleEndExam = async () => {
    if (!studentExamId) {
      toast.error("Student exam ID not found. Please start the exam again.");
      return;
    }

    setIsSubmittingExam(true);
    try {
      // Call end-exam API using student exam ID from localStorage
      const response = await EndIFRSExam(studentExamId);

      if (response.error) {
        console.error("Error ending exam:", response.error);
        toast.error(response.error.message || "Failed to end exam");
        setIsSubmittingExam(false);
        return;
      }

      toast.success("Exam ended successfully!");
      navigate(`/ifrs-exam/${exam_id}/results`);
    } catch (error) {
      console.error("Unexpected error ending exam:", error);
      toast.error("An unexpected error occurred while ending the exam");
      setIsSubmittingExam(false);
    }
  };

  // Handle submission for Word Processor using useMutation
  // Uses API: student/ifrs-question/:student_ifrs_exam_id/response-oprtions/:response_opt_id
  const { mutate: submitWordProcessor, isPending: isSubmittingWordProcessor } = useMutation({
    mutationFn: (responseOptionId: string) => {
      if (!studentExamId || !currentQuestionId) {
        throw new Error("Missing required information");
      }
      return SubmitIFRSQuestionResponse(
        studentExamId,
        responseOptionId,
        {
          ifrsQuestionId: currentQuestionId,
          answerData: {
            type: "wordProcessor",
            data: wordProcessor.content,
          },
        }
      );
    },
    onSuccess: (response) => {
      if (response.error) {
        console.error("Error submitting answer:", response.error);
        toast.error(response.error.message || "Failed to submit answer");
      } else {
        console.log("Answer submitted successfully:", response.data);
        toast.success("Answer submitted successfully!");
        wordProcessor.setIsOpen(false);
      }
    },
    onError: (error: any) => {
      console.error("Unexpected error:", error);
      toast.error(error.message || "An unexpected error occurred");
    },
  });

  const handleWordProcessorSubmit = (responseOptionId: string) => {
    if (!studentExamId || !currentQuestionId) return;
    submitWordProcessor(responseOptionId);
  };

  // Handle submission for Spreadsheet using useMutation
  // Uses API: student/ifrs-question/:student_ifrs_exam_id/response-oprtions/:response_opt_id
  // Uses student_exam_id from localStorage (not exam_id from URL params)
  // const { mutate: submitSpreadsheet, isPending: isSubmittingSpreadsheetData } = useMutation({
  //   mutationFn: (responseOptionId: string) => {
  //     if (!studentExamId || !currentQuestionId) {
  //       throw new Error("Student exam ID not found. Please start the exam again.");
  //     }

  //     // Transform spreadsheet data to only include non-empty cells with col, row, value
  //     const cellData: Array<{ col: number; row: number; value: string }> = [];

  //     spreadsheet.data.forEach((rowData, rowIndex) => {
  //       rowData.forEach((cellValue, colIndex) => {
  //         // Only include cells that have a value
  //         if (
  //           cellValue !== null &&
  //           cellValue !== undefined &&
  //           cellValue !== ""
  //         ) {
  //           cellData.push({
  //             col: colIndex,
  //             row: rowIndex,
  //             value: String(cellValue),
  //           });
  //         }
  //       });
  //     });

  //     return SubmitIFRSQuestionResponse(
  //       studentExamId,
  //       responseOptionId,
  //       {
  //         ifrsQuestionId: currentQuestionId,
  //         answerData: {
  //           type: "spreadsheet",
  //           data: cellData,
  //         },
  //       }
  //     );
  //   },
  //   onSuccess: (response) => {
  //     if (response.error) {
  //       console.error("Error submitting answer:", response.error);
  //       toast.error(response.error.message || "Failed to submit answer");
  //     } else {
  //       console.log("Answer submitted successfully:", response.data);
  //       toast.success("Answer submitted successfully!");
  //       spreadsheet.setIsOpen(false);
  //     }
  //   },
  //   onError: (error: any) => {
  //     console.error("Unexpected error:", error);
  //     toast.error(error.message || "An unexpected error occurred");
  //   },
  // });

  // const handleSpreadsheetSubmit = (responseOptionId: string) => {
  //   if (!studentExamId || !currentQuestionId) {
  //     if (!studentExamId) {
  //       toast.error("Student exam ID not found. Please start the exam again.");
  //     }
  //     return;
  //   }
  //   submitSpreadsheet(responseOptionId);
  // };

  // Spreadsheet answers are worked on in Excel, not in the browser: the student
  // downloads the template, fills it in, and uploads the file back. The upload
  // itself goes to S3 (SpreadsheetFileUpload), and only the resulting URL is
  // recorded against the response option.
  const { mutate: submitSpreadsheetAnswer } = useMutation({
    mutationFn: (fileUrl: string) => {
      if (!studentExamId || !currentQuestionId || !currentUploadOption) {
        throw new Error("Missing required information");
      }
      return SubmitIFRSQuestionResponse(
        studentExamId,
        currentUploadOption.id,
        {
          ifrsQuestionId: currentQuestionId,
          answerFilePath: fileUrl,
        }
      );
    },
    onSuccess: (response) => {
      if (response.error) {
        console.error("Error submitting answer:", response.error);
        toast.error(response.error.message || "Failed to submit answer");
      } else {
        toast.success("Answer submitted successfully!");
        setUploadModalOpen(false);
        setCurrentUploadOption(null);
      }
    },
    onError: (error: any) => {
      console.error("Unexpected error:", error);
      toast.error(error.message || "An unexpected error occurred");
    },
  });

  const handleSpreadsheetFileUpload = (fileUrl: string) => {
    submitSpreadsheetAnswer(fileUrl);
  };

  useEffect(() => {
    if (
      spreadsheet.isOpen &&
      spreadsheet.spreadsheetRef.current &&
      spreadsheet.result?.worksheets?.[0]?.styles &&
      !spreadsheet.isLoading
    ) {
      const timer = setTimeout(() => {
        const instance: any = spreadsheet.spreadsheetRef.current;
        const styles = spreadsheet.result.worksheets[0].styles;

        console.log("Applying styles:", Object.keys(styles).length, "cells");

        try {
          Object.keys(styles).forEach((cellKey) => {
            try {
              instance.setStyle(cellKey, styles[cellKey]);
            } catch (err) {
              console.error(`Failed to set style for ${cellKey}:`, err);
            }
          });
        } catch (error) {
          console.error("Error applying styles:", error);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    spreadsheet.isOpen,
    spreadsheet.result?.worksheets,
    spreadsheet.spreadsheetRef.current,
    spreadsheet.isLoading,
    spreadsheet.mountKey,
  ]);
  return (
    <section className="exam-page-container">
      <div className="exam-page-header">
        <div className="controls-list">
          <div className={`control highlight-selector`}>
            <div
              className="color-box-container"
              onClick={() => {
                symbol.saveSelection();
                symbol.setIsOpen(true);
              }}
            >
              <Svgify IconName="symbol" FontWeight="stroke" />
              Symbol
            </div>
          </div>

          <div
            className={`control highlight-selector ${hightlight.isOpen ? "open" : ""
              }`}
            ref={hightlight.colorBoxContainerRef}
          >
            <div
              className="color-box-container"
              onClick={() => {
                hightlight.saveSelection();
                hightlight.setIsOpen(true);
              }}
            >
              <div
                className={`color-box bordered color-${hightlight.color}`}
              ></div>
              <div
                className="dropdown-list"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="color-box-list">
                  <button
                    className="color-box color-yellow"
                    onClick={() => hightlight.setColor("yellow")}
                  ></button>
                  <button
                    className="color-box color-green"
                    onClick={() => hightlight.setColor("green")}
                  ></button>
                  <button
                    className="color-box color-red"
                    onClick={() => hightlight.setColor("red")}
                  ></button>
                  <button
                    className="color-box color-blue"
                    onClick={() => hightlight.setColor("blue")}
                  ></button>
                </div>
                <div className="color-box-list">
                  <button onClick={() => hightlight.setColor("transparent")}>
                    <div className="color-box bordered color-transparent"></div>
                    <span>Remove Highlight</span>
                  </button>
                </div>
              </div>
            </div>
            <span
              className="triangleDown"
              onClick={() => {
                hightlight.saveSelection();
                hightlight.setIsOpen((o) => !o);
              }}
            ></span>
            <button
              className="control-button"
              onMouseDown={(e) => {
                // Prevent focus loss to button
                e.preventDefault();
                hightlight.saveSelection();
              }}
              onClick={hightlight.highLightSelections}
            >
              Highlight
            </button>
          </div>

          <div className="control">
            <button
              className="control-button"
              onMouseDown={(e) => {
                e.preventDefault();
                strikethrough.saveSelection();
              }}
              onClick={strikethrough.toggleStrikethrough}
            >
              <span>
                <Svgify IconName="t-strike" FontWeight="fill" />
              </span>{" "}
              Strikethrough
            </button>
          </div>

          <div className="control">
            <button
              className="control-button"
              onClick={() => calculator.setIsOpen((o) => !o)}
            >
              <span>
                <Svgify IconName="calculator" FontWeight="both" />
              </span>{" "}
              Calculator
            </button>
          </div>

          <div className="control">
            <button
              className="control-button"
              onClick={() => scratchPad.setIsOpen(true)}
            >
              <span>
                <Svgify IconName="scratch-pad" FontWeight="fill" />
              </span>{" "}
              Scratch Pad
            </button>
          </div>
        </div>

        <div className="actions-list">
          <div className="control">
            <button
              className="control-button"
              onClick={() => closeAllWindows()}
            >
              <Svgify IconName="close" FontWeight="fill" />
              Close All
            </button>
          </div>

          <div className="control">
            <button
              className="control-button"
              onClick={() => currentQuestionId && toggleFlag(currentQuestionId)}
            >
              {isQuestionFlagged && (
                <Svgify IconName="flag" FontWeight={"both"} />
              )}
              {!isQuestionFlagged && (
                <Svgify IconName="flag" FontWeight={"stroke"} />
              )}
              Flag For Review
            </button>
          </div>
        </div>
      </div>

      <div className="exam-page-content">
        <aside className="questions-data-list" ref={sidebarRef}>
        <span
          className="resizer"
          onMouseDown={() => {
            isResizingSidebar.current = true;
            document.body.style.cursor = "ew-resize";
            document.body.style.userSelect = "none";
          }}
></span>          {(exhibitsData?.data?.length ?? 0) > 0 && (
            <>
              <h4 className="!text-[16px]">Exhibits</h4>
              <ul>
                {exhibitsData?.data?.map((exhibit: IFRSQuestionExhibitDTO) => (
                  <li key={exhibit.id}>
                    <button onClick={() => handleViewExhibit(exhibit)}>
                      <Svgify IconName="exhibit" FontWeight="both" />
                      {exhibit.title}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {questionData?.requirements?.trim() && (
            <>
              <h4 className="!text-[16px]">Requirements</h4>
              <ul>
                <li>
                  <button onClick={() => setRequirementsModalOpen(true)}>
                    <Svgify IconName="requirement" FontWeight="both" />
                    Requirement {questionData?.marks && (`(${questionData.marks} marks)`)}
                  </button>
                </li>
              </ul>
            </>
          )}

          {(responseOptionsData?.data?.length ?? 0) > 0 && (
            <>
          <h4 className="!text-[16px]">Response Option</h4>
          <ul>
            {responseOptionsData?.data?.map(
              (option: IFRSQuestionResponseOptionDTO) => (
                <li key={option.id} >
                  {option.fileType === "spreadsheet" ? (
                    <div className="!border-none bg-[#f2f2f2]" style={{ display: "flex", flexDirection: "row"}}>
                      <a className="!border-none flex-1" href={option.answerFileUrl || "/empty.xlsx"} download>
 <button
                        // onClick={() =>
                        //   spreadsheet.handleOpenSpreadsheet(option) // Pass only the option
                        // }
                      >
                        <Svgify IconName={"spreadsheet"} FontWeight="fill" />
                        Spreadsheet
                      </button>
                      </a>
                      <button
                        onClick={() => {
                          setCurrentUploadOption(option);
                          setUploadModalOpen(true);
                        }}
                        className="!w-fit !p-1 !border-none text-sm !h-fit my-auto !bg-gray-300 rounded-[8px] mr-2"
                      >
                        Submit
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleOpenWordProcessor(option)}
                    >
                      <Svgify IconName={"docs"} FontWeight="fill" />
                      Word Processor
                    </button>
                  )}
                </li>
              )
            )}
          </ul>
            </>
          )}
        </aside>

        <div
          dangerouslySetInnerHTML={{
            __html: questionData?.description || "",
          }}
          className="page-content default-text-previewer"
        ></div>
      </div>

      <Modal
        header="Calculator"
        isOpen={calculator.isOpen}
        closeModal={() => calculator.setIsOpen(false)}
      >
        <div style={{
          width: "100%",
          height: "600px",
          overflow: "hidden",
          position: "relative"
        }}>
          <iframe
            src="https://ti84calc.com/ti30calc"
            ref={calculator.iframeRef}
            onLoad={calculator.onLoad}
            style={{
              width: "100%",
              height: "calc(100% + 200px)",
              minHeight: "800px",
              border: "none",
              position: "absolute",
              top: "-120px",
              left: "0",
              pointerEvents: "auto",
            }}
            allow="fullscreen"
          ></iframe>
          {/* Overlay to block top area links if any slip through */}
          <div style={{
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            height: "10px",
            background: "transparent",
            pointerEvents: "none",
            zIndex: 10,
          }}></div>
          {/* Overlay to block bottom area footer if any slip through */}
          <div style={{
            position: "absolute",
            bottom: "0",
            left: "0",
            right: "0",
            height: "10px",
            background: "transparent",
            pointerEvents: "none",
            zIndex: 10,
          }}></div>
        </div>
      </Modal>

      <Modal
        header="Symbols"
        isOpen={symbol.isOpen}
        closeModal={() => symbol.setIsOpen(false)}
      >
        <div
          className="symbols-list"
          style={{
            display: "flex",
            gap: "0.5rem",
            padding: "1rem",
            justifyContent: "center",
            width: "fit-content",
            maxWidth: "300px",
            height: "80px",
            maxHeight: "120px",
          }}
        >
          {[
            "$",
            "€",
            "£",
            "¥",
            "₱",
          ].map((char) => (
            <button
              key={char}
              className="symbol"
              onClick={() => symbol.insertSymbol(char)}
              style={{
                padding: "0.5rem",
                fontSize: "1rem",
                cursor: "pointer",
                border: "1px solid #333",
                backgroundColor: "#fff",
                borderRadius: "2px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title={`Insert ${char}`}
            >
              {char}
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        header="Spreadsheet"
        isOpen={spreadsheet.isOpen}
        closeModal={() => {
          spreadsheet.setIsOpen(false);
          spreadsheet.setCurrentResponseOption(null);
          spreadsheet.reset(); // Add this to clear state
          console.log("called")
        }}
      >
        <div
          className="spreadsheet-view"
          style={{
            overflow: "auto",
            width: "100%",
            minHeight: "500px",
            backgroundColor: "#fff",
          }}
        >
          {spreadsheet.isLoading ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <Spinner />
              <p>Loading spreadsheet...</p>
            </div>
          ) : spreadsheet.result?.worksheets &&
            spreadsheet.result.worksheets.length > 0 ? (
            <div style={{ width: "100%", minHeight: "500px" }}>
              <Spreadsheet
                key={`spreadsheet-${spreadsheet.currentResponseOption?.id}`}
                ref={spreadsheet.spreadsheetRef} // Use spreadsheetRef directly
                tabs={spreadsheet.result.worksheets.length > 1}
                toolbar={true}
              >
                {spreadsheet.result.worksheets.map(
                  (sheet: any, idx: number) => (
                    <Worksheet
                      key={`worksheet-${idx}-${spreadsheet.currentResponseOption?.id}`}
                      title={sheet.name || `Sheet ${idx + 1}`}
                      data={sheet.data}
                      columns={sheet.columns || []}
                      style={sheet.styles || {}} // CRITICAL: Pass styles here
                      minDimensions={[25, 15]}
                    // onChange={(instance: any) => {
                    //   if (idx === 0 && instance?.data) {
                    //     spreadsheet.setData(instance.data);
                    //   }
                    // }}
                    />
                  )
                )}
              </Spreadsheet>
            </div>
          ) : (
            <div
              style={{
                padding: "2rem",
                textAlign: "center",
                minHeight: "400px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p>No spreadsheet data available</p>
              <button
                onClick={() => {
                  const emptyData = Array.from({ length: 25 }, () =>
                    Array.from({ length: 15 }, () => "")
                  );
                  const emptyColumns = Array.from({ length: 15 }, () => ({
                    type: "text",
                    width: 100,
                  }));

                  spreadsheet.setResult({
                    worksheets: [
                      {
                        name: "Sheet1",
                        data: emptyData,
                        columns: emptyColumns,
                      },
                    ],
                  });
                  // spreadsheet.setData(emptyData);
                }}
                style={{
                  marginTop: "1rem",
                  padding: "0.5rem 1rem",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Create Empty Spreadsheet
              </button>
            </div>
          )}
        </div>

        <div
          style={{
            padding: "1rem",
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f8f9fa",
          }}
        >
          <span style={{ fontSize: "0.875rem", color: "#666" }}>
            Spreadsheet Response
          </span>

          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={() => spreadsheet.setIsOpen(false)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>

          </div>
        </div>
      </Modal>

      {/* Open Exhibits (Multiple instances allowed) */}
      {openExhibits.map((exhibit) => {
        console.log("exhibit", exhibit);
        return exhibit.type === "pdf" ? (
          <div key={exhibit.id} data-exhibit-id={exhibit.id}>
            <PDFViewerModal
              key={exhibit.id}
              isOpen={true}
              onClose={() => closeExhibit(exhibit.id)}
              pdfUrl={exhibit.pdfUrl}
              title={exhibit.title}
              onRegister={(fn) => { exhibitFocusRef.current[exhibit.id] = fn; }}

            />
          </div>
        ) : (
          <Modal
            key={exhibit.id}
            header={exhibit.title || "Spreadsheet Exhibit"}
            isOpen={true}
            closeModal={() => closeExhibit(exhibit.id)}
            resizable={true}
            onRegister={(fn) => { exhibitFocusRef.current[exhibit.id] = fn; }}

          >
            <div
              className="spreadsheet-view"
              style={{
                overflow: "auto",
                maxHeight: "80svh",
                maxWidth: "80svw",
              }}
            >
              {exhibit.isLoading ? (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  Loading spreadsheet...
                </div>
              ) : exhibit.sheets && exhibit.sheets.length > 0 ? (
                <Spreadsheet
                  key={`${exhibit.id}-${exhibit.sheets.length}`}
                  tabs={exhibit.sheets.length > 1}
                  toolbar={false}
                  ref={(el: any) => {
                    if (el) {
                      spreadsheetRefs.current[exhibit.id] = el;
                    }
                  }}
                >
                  {exhibit.sheets.map((sheet: any) => (
                    <Worksheet
                      key={sheet.name}
                      data={sheet.data}
                      columns={sheet.columns}
                      minDimensions={[
                        Math.max(sheet.data.length, 20),
                        Math.max(sheet.data[0].length, 20),
                      ]}
                      title={sheet.name}
                    />
                  ))}
                </Spreadsheet>
              ) : (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  No spreadsheet data available
                </div>
              )}
            </div>
          </Modal>
        );
      })}

      {/* Scratch Pad Modal */}
      <ScratchPadModal
        isOpen={scratchPad.isOpen}
        onClose={() => scratchPad.setIsOpen(false)}
        content={scratchPad.content}
        onChange={scratchPad.setContent}
      />

      {/* Word Processor Modal */}
      <WordProcessorModal
        isOpen={wordProcessor.isOpen}
        onClose={() => wordProcessor.setIsOpen(false)}
        content={wordProcessor.content}
        onChange={wordProcessor.setContent}
        isLoading={wordProcessor.isLoading}
        onSubmit={
          wordProcessor.currentResponseOption
            ? () =>
              handleWordProcessorSubmit(
                wordProcessor.currentResponseOption!.id
              )
            : undefined
        }
        isSubmitting={isSubmittingWordProcessor}
      />

      {/* Exam Footer */}
      <footer className="exam-footer">
        <button
          className="exam-footer-button"
          onClick={() => setNavigatorModalOpen(true)}
        >
          <Svgify IconName="navigator" />
          Navigator
        </button>
          {!isFirstQuestion && (
    <button             className="exam-footer-button"
 onClick={handlePrevQuestion}>
                  <ArrowLeft size={18} />

      Previous
    </button>
  )}
        {isLastQuestion ? (
          <button
            className="exam-footer-button"
            onClick={handleEndExam}
            disabled={isSubmittingExam}
          >
            {isSubmittingExam ? "Ending..." : "Finish Exam"}
          </button>
        ) : (
          <button className="exam-footer-button" onClick={handleNextQuestion}>
            Next
            <ArrowRight size={18} />
          </button>
        )}
      </footer>

      {/* Navigator Modal */}
      <Modal
        header="Navigator - select a question to go to it"
        isOpen={navigatorModalOpen}
        closeModal={() => setNavigatorModalOpen(false)}
        resizable={true}
      >
        <div style={{ padding: "1rem" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  backgroundColor: "#f0f0f0",
                  borderBottom: "2px solid #ccc",
                }}
              >
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "left",
                    borderRight: "1px solid #ccc",
                    borderRadius: "0",
                  }}
                >
                  Question #
                </th>
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "left",
                    borderRight: "1px solid #ccc",
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "left",
                    borderRadius: "0",
                  }}
                >
                  Flagged - Review
                </th>
              </tr>
            </thead>
            <tbody>
              {(questions as any)?.map((q: any, index: number) => (
                <tr
                  key={q.id}
                  onClick={() => {
                    handleGoToQuestion(index);
                    setNavigatorModalOpen(false);
                  }}
                  style={{
                    backgroundColor:
                      q.id === currentQuestionId
                        ? "#c8e6e6"
                        : index % 2 === 0
                          ? "#f9f9f9"
                          : "#fff",
                    cursor: "pointer",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <td
                    style={{
                      padding: "0.75rem",
                      borderRight: "1px solid #ddd",
                    }}
                  >
                    Question {index + 1}
                  </td>
                  <td
                    style={{
                      padding: "0.75rem",
                      borderRight: "1px solid #ddd",
                      color: isSeen(q.id) ? "#2e7d32" : "#d32f2f",
                    }}
                  >
                    {q.hasSubmitted
                      ? "Attempted"
                      : isSeen(q.id)
                        ? "Seen"
                        : "Unseen"}
                  </td>
                  <td style={{ padding: "0.75rem", textAlign: "center" }}>
                    {checkQuestionFlagged(q.id) && (
                      <Svgify IconName="flag" FontWeight={"both"} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>

      {/* Requirements Modal */}
      <Modal
        header={`Requirement ${questionData?.marks ? `(${questionData.marks} marks)` : ""}`}
        isOpen={requirementsModalOpen}
        closeModal={() => setRequirementsModalOpen(false)}
        resizable={true}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: questionData?.requirements || "",
          }}
          className="p-2 bg-white default-text-previewer"
          style={{ minHeight: "250px", height: "100%", userSelect: "auto" }}
        ></div>
      </Modal>

      {/* File Upload Modal */}
      <Modal
        header="Upload Spreadsheet Answer"
        isOpen={uploadModalOpen}
        closeModal={() => {
          setUploadModalOpen(false);
          setCurrentUploadOption(null);
        }}
      >
        <SpreadsheetFileUpload
          onFileUploaded={handleSpreadsheetFileUpload}
          onCancel={() => {
            setUploadModalOpen(false);
            setCurrentUploadOption(null);
          }}
        />
      </Modal>
    </section>
  );
};

export default ExamPage;
